<template>
	<div>
		<div>
			<md-radio
				style="margin-bottom: 0;"
				:class="{ 'md-error': errors.has('evaluateCompanies') }"
				v-model="evaluationData.evaluateCompanies"
				:value="true"
				@change="onEvaluateCompaniesChanged"
				v-validate="modelValidations.evaluateCompanies"
				data-vv-name="evaluateCompanies"
				:required="modelValidations.evaluateCompanies.required"
			>
				{{ $t(evaluateLabelKey) }}
			</md-radio>
			<a class="pointer margin_r" @click="evaluateAll">
				{{ $t("common.selectAll") }}
			</a>
			<span v-if="errors.has('evaluateCompanies')" class="validation-error validation-inline">{{ errors.first('evaluateCompanies') }}</span>
			
			<div class="margin_l margin_top">
				<!-- Single error message is enough if no option was selected -->
				<span v-if="errors.has('evaluateSellerIds')" class="validation-error margin_bottom">{{ errors.first('evaluateSellerIds') }}</span>
				<!-- Checkbox list -->
				<div class="flex flex_column scroll_y_auto" :style="companyListMaxHeightStyle">
					<md-checkbox v-for="seller in sellers"
								 :key="'seller-' + seller.id"
								 style="margin-top: 0;"
								 :class="{ 'md-error': errors.has('evaluateSellerIds') }"
								 v-model="evaluationData.evaluateSellerIds"
								 :value="seller.id"
								 @change="onCompanyChecked"
								 v-validate="modelValidations.evaluateSellerIds"
								 data-vv-name="evaluateSellerIds"
					>
						<span v-html="$t('marketSoundingDecision.companyEvaluation.choice.seller', { sellerName: seller.name, optionalStatusPrefix: getOptionalStatusPrefix(seller) })"></span>
					</md-checkbox>
					<md-checkbox v-for="debtor in debtors"
								 :key="'debtor-' + debtor.id"
								 style="margin-top: 0;"
								 :class="{ 'md-error': errors.has('evaluateDebtorIds') }"
								 v-model="evaluationData.evaluateDebtorIds"
								 :value="debtor.id"
								 @change="onCompanyChecked"
								 v-validate="modelValidations.evaluateDebtorIds"
								 data-vv-name="evaluateDebtorIds"
					>
						<span v-html="$t('marketSoundingDecision.companyEvaluation.choice.debtor', { debtorName: debtor.name, optionalStatusPrefix: getOptionalStatusPrefix(debtor) })"></span>
					</md-checkbox>
				</div>
			</div>
		</div>
		<div>
			<md-radio
				:class="{ 'md-error': errors.has('evaluateCompanies') }"
				v-model="evaluationData.evaluateCompanies"
				:value="false"
				@change="onEvaluateCompaniesChanged"
				v-validate="modelValidations.evaluateCompanies"
				data-vv-name="evaluateCompanies"
				:required="modelValidations.evaluateCompanies.required"
			>
				{{ $t(evaluateNotLabelKey) }}
			</md-radio>
			<span v-if="errors.has('evaluateCompanies')" class="validation-error validation-inline">{{ errors.first('evaluateCompanies') }}</span>
		</div>
	</div>
</template>

<script>
import { createInterdependentValidationWatchers } from '@/validation/validation';
import {mapGetters} from "vuex";
import {Validator} from "vee-validate";
import {formatAsLocalizedEnum} from "@/common/format";
import {enumDef as enumDefs} from "@/common/enumDefs";

export default {
	name: 'MarketSoundingDecisionCompanyEvaluation',
 props: {
		sellers: { type: Array, default: [] },
		debtors: { type: Array, default: [] },
		evaluationData: {
			type: Object,
			default: {
				evaluateCompanies: null,
				evaluateSellerIds: [],
				evaluateDebtorIds: []
			}
		},
		evaluationPreferenceKey: { type: String, required: true }, // Preference key determines which local preference to read: e.g., 'approveCompaniesOnDecision' or 'rejectCompaniesOnDecision'
		evaluateLabelKey: { type: String, required: true }, // Label key for approve/reject choice
		evaluateNotLabelKey: { type: String, required: true }, // Label key for don't approve/reject choice
		maxCompanyListHeight: { type: Number, default: 300 }, // Max height for company list, after which scrolling is shown
		validator: { type: Validator, required: true } // Parent validator instance since the default Vue inject mechanism leads to problems with the modal this is embedded in
	},
	data() {
		return {
			modelValidations: {
				evaluateCompanies: {
					required: true
				},
				evaluateSellerIds: {
					checked_any: {
						conditionFunc: () => this.evaluationData.evaluateCompanies,
						fieldNames: ['evaluateSellerIds', 'evaluateDebtorIds'],
						dataGetter: () => this.evaluationData,
					},
				},
				evaluateDebtorIds: {
					checked_any: {
						conditionFunc: () => this.evaluationData.evaluateCompanies,
						fieldNames: ['evaluateSellerIds', 'evaluateDebtorIds'],
						dataGetter: () => this.evaluationData,
					},
				},
			}
		};
	},
	created() {
		// Apply stored default for now only if no evaluation was chosen before
		if (this.investorPreferences.marketSounding[this.evaluationPreferenceKey] === false) {
			this.evaluationData.evaluateCompanies = false;
		}

		// Update the internal validator instance
		this.$validator = this.validator;

		// Make sure checkboxes are cross-validated correctly
		createInterdependentValidationWatchers(this, this.$validator, ['evaluateCompanies', 'evaluateSellerIds', 'evaluateDebtorIds'], "evaluationData");
	},
	computed: {
		...mapGetters({
			investorPreferences: "local/investorPreferences"
		}),
		companyListMaxHeightStyle() {
			return `max-height: ${this.maxCompanyListHeight}px;`;
		},
	},
	methods: {
		getOptionalStatusPrefix(company) {
			// Only show the status prefix if the seller has the opposite evaluation status currently. For unevaluated sellers, no status is shown.
			return company.isEvaluated && company.hasDifferentEvaluationStatus
				? `<b>(${formatAsLocalizedEnum(enumDefs.InvestorSellerDebtorConfigStatus, company.evaluationStatus)})</b>&nbsp;`
				: '';
		},
		evaluateAll() {
			this.evaluationData.evaluateSellerIds = this.sellers.map(seller => seller.id);
			this.evaluationData.evaluateDebtorIds = this.debtors.map(debtor => debtor.id);
			this.onCompanyChecked();
		},
		onEvaluateCompaniesChanged() {
			if (!this.evaluationData.evaluateCompanies) {
				this.evaluationData.evaluateSellerIds = [];
				this.evaluationData.evaluateDebtorIds = [];
			}
		},
		onCompanyChecked() {
			const hasAnyCompanyChecked = this.evaluationData.evaluateSellerIds.length > 0 || this.evaluationData.evaluateDebtorIds.length > 0;

			if (hasAnyCompanyChecked && !this.evaluationData.evaluateCompanies) {
				this.evaluationData.evaluateCompanies = true;
			}
		}
	}
};
</script>
